import React, { useState, useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import { PageHeader, FilterTabs, StatusChip, MetricCard, EmptyState, LoadingState, DataTable, Column } from '@/components/shared';
import TrioButton from '@/trio-ui/Button';
import { FxSubscription, FxVendor } from '@/types/api/finance';

type SortDir = 'asc' | 'desc' | null;

type Props = {
  subscriptions: FxSubscription[];
  vendors: FxVendor[];
  isLoading: boolean;
  showForm: boolean;
  editingSub: FxSubscription | null;
  onNewSub: () => void;
  onEdit: (sub: FxSubscription) => void;
  onCancel: (sub: FxSubscription) => void;
  onDelete: (sub: FxSubscription) => void;
  onSubmit: (data: any) => void;
  onCloseForm: () => void;
  isSubmitting: boolean;
  error: string | null;
};

const statusMap = {
  active: { label: 'Active', color: '#2AD062', variant: 'filled' as const },
  trial: { label: 'Trial', color: '#054AD9', variant: 'filled' as const },
  paused: { label: 'Paused', color: '#FFD166', variant: 'filled' as const },
  cancelled: { label: 'Cancelled', color: '#FF5E54', variant: 'outlined' as const },
};

const formatCurrency = (amount: number, currency = 'USD'): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const FinanceXSubscriptionsComponent = function ({
  subscriptions,
  vendors,
  isLoading,
  showForm,
  editingSub,
  onNewSub,
  onEdit,
  onCancel,
  onDelete,
  onSubmit,
  onCloseForm,
  isSubmitting,
  error,
}: Props): JSX.Element {
  const [confirmDelete, setConfirmDelete] = useState<FxSubscription | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<FxSubscription | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [costSort, setCostSort] = useState<SortDir>(null);
  const [collapsedVendors, setCollapsedVendors] = useState<Set<string>>(new Set());

  const filterTabs = useMemo(() => {
    const counts: Record<string, number> = { all: subscriptions.length };
    ['active', 'trial', 'paused', 'cancelled'].forEach((s) => {
      counts[s] = subscriptions.filter((sub) => sub.status === s).length;
    });
    return [
      { key: 'all', label: 'All', count: counts.all },
      { key: 'active', label: 'Active', count: counts.active },
      { key: 'trial', label: 'Trial', count: counts.trial },
      { key: 'paused', label: 'Paused', count: counts.paused },
      { key: 'cancelled', label: 'Cancelled', count: counts.cancelled },
    ];
  }, [subscriptions]);

  const filteredAndSorted = useMemo(() => {
    let list = subscriptions;
    if (statusFilter !== 'all') list = list.filter((s) => s.status === statusFilter);
    if (costSort) list = [...list].sort((a, b) => costSort === 'asc' ? a.amount - b.amount : b.amount - a.amount);
    return list;
  }, [subscriptions, statusFilter, costSort]);

  type VendorGroup = { vendorId: string; vendorName: string; subs: FxSubscription[]; totalMonthly: number };

  const vendorGroups = useMemo(() => {
    const map = new Map<string, VendorGroup>();
    filteredAndSorted.forEach((sub) => {
      const vid = sub.vendorId ?? '__none__';
      const existing = map.get(vid);
      if (existing) { existing.subs.push(sub); }
      else { map.set(vid, { vendorId: vid, vendorName: sub.vendor?.displayName || sub.vendor?.name || 'No Vendor', subs: [sub], totalMonthly: 0 }); }
    });
    const groupsWithTotals = new Map<string, VendorGroup>();
    map.forEach((group, key) => {
      const totalMonthly = group.subs.filter((s) => s.status === 'active' || s.status === 'trial').reduce((sum, s) => {
        if (s.billingCycle === 'monthly') return sum + s.amount;
        if (s.billingCycle === 'quarterly') return sum + s.amount / 3;
        if (s.billingCycle === 'annual') return sum + s.amount / 12;
        return sum + s.amount;
      }, 0);
      groupsWithTotals.set(key, { ...group, totalMonthly });
    });
    let groups = Array.from(groupsWithTotals.values());
    if (costSort) groups = groups.sort((a, b) => costSort === 'asc' ? a.totalMonthly - b.totalMonthly : b.totalMonthly - a.totalMonthly);
    return groups;
  }, [filteredAndSorted, costSort]);

  const toggleVendorCollapse = (vendorId: string): void => {
    setCollapsedVendors((prev) => { const next = new Set(prev); if (next.has(vendorId)) next.delete(vendorId); else next.add(vendorId); return next; });
  };

  const totalMonthly = subscriptions.filter((s) => s.status === 'active').reduce((sum, s) => {
    if (s.billingCycle === 'monthly') return sum + s.amount;
    if (s.billingCycle === 'quarterly') return sum + s.amount / 3;
    if (s.billingCycle === 'annual') return sum + s.amount / 12;
    return sum;
  }, 0);

  const activeCount = subscriptions.filter((s) => s.status === 'active').length;

  const subColumns: Column<FxSubscription>[] = [
    { id: 'name', label: 'Name', render: (sub) => <span style={{ fontWeight: 600, fontSize: 13 }}>{sub.name}</span> },
    { id: 'vendor', label: 'Vendor', render: (sub) => <span style={{ fontSize: 13, color: '#71717A' }}>{sub.vendor?.displayName || sub.vendor?.name || '—'}</span> },
    {
      id: 'cost', label: (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover': { color: '#054AD9' } }}
          onClick={() => {
            const nextSort = (prev: SortDir): SortDir => {
              if (prev === 'desc') return 'asc';
              if (prev === 'asc') return null;
              return 'desc';
            };
            setCostSort(nextSort);
          }}>
          Cost
          {costSort === 'desc' && <ArrowDownwardIcon sx={{ fontSize: 14 }} />}
          {costSort === 'asc' && <ArrowUpwardIcon sx={{ fontSize: 14 }} />}
        </Box>
      ) as any,
      render: (sub) => <span style={{ fontWeight: 500, fontSize: 13 }}>{formatCurrency(sub.amount, sub.currency)}</span>,
    },
    { id: 'cycle', label: 'Cycle', render: (sub) => <span style={{ fontSize: 13, textTransform: 'capitalize' }}>{sub.billingCycle}</span> },
    { id: 'status', label: 'Status', render: (sub) => <StatusChip status={sub.status} statusMap={statusMap} size="small" /> },
    { id: 'nextBilling', label: 'Next Billing', render: (sub) => <span style={{ color: '#71717A', fontSize: 13 }}>{sub.nextBilling ?? '—'}</span> },
    { id: 'actions', label: '', align: 'right', width: '100px', render: (sub) => (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="Edit"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(sub); }}><EditIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        {sub.status !== 'cancelled' && (
          <Tooltip title="Cancel"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setConfirmCancel(sub); }} sx={{ color: '#FFD166' }}><CancelIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        )}
        <Tooltip title="Delete"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setConfirmDelete(sub); }} sx={{ color: '#FF5E54' }}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      </Box>
    )},
  ];

  return (
    <Box sx={{ padding: '40px' }}>
      <PageHeader
        title="Subscriptions"
        actions={
          <TrioButton variantColor="blue" size="small" onClick={onNewSub}>
            <AddIcon sx={{ fontSize: 18, mr: 0.5 }} /> Add Subscription
          </TrioButton>
        }
      />

      {/* Summary metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
        <MetricCard label="Monthly Spend" value={formatCurrency(totalMonthly)} color="#054AD9" isLoading={isLoading} />
        <MetricCard label="Annual Projection" value={formatCurrency(totalMonthly * 12)} color="#FF5E54" isLoading={isLoading} />
        <MetricCard label="Active" value={String(activeCount)} color="#2AD062" indicator={<AutorenewIcon sx={{ fontSize: 16 }} />} isLoading={isLoading} />
      </Box>

      {/* Filter tabs */}
      {!isLoading && subscriptions.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <FilterTabs tabs={filterTabs} activeKey={statusFilter} onChange={setStatusFilter} color="#054AD9" />
        </Box>
      )}

      {(() => {
        if (isLoading) return <LoadingState message="Loading subscriptions..." />;
        if (subscriptions.length === 0) {
          return (
            <EmptyState
              icon={AutorenewIcon}
              title="No subscriptions yet"
              description="Add your first SaaS subscription to start tracking costs."
              action={{ label: 'Add Subscription', onClick: onNewSub }}
            />
          );
        }
        if (filteredAndSorted.length === 0) {
          return <EmptyState title="No matches" description="No subscriptions match this filter." />;
        }
        return (
          <DataTable<FxSubscription>
            columns={subColumns}
            data={filteredAndSorted}
            rowKey={(s) => s.id}
            isLoading={false}
            emptyMessage="No subscriptions"
            emptyIcon={AutorenewIcon}
          />
        );
      })()}

      {/* Add/Edit Subscription Dialog */}
      <Dialog open={showForm} onClose={onCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingSub ? 'Edit Subscription' : 'Add Subscription'}</DialogTitle>
        <Formik
          initialValues={{
            vendorId: editingSub?.vendorId ?? '', newVendorName: '', name: editingSub?.name ?? '',
            amount: editingSub?.amount ?? '', currency: editingSub?.currency ?? 'USD',
            billingCycle: editingSub?.billingCycle ?? 'monthly', startDate: editingSub?.startDate ?? new Date().toISOString().split('T')[0],
            nextBilling: editingSub?.nextBilling ?? '', seats: editingSub?.seats ?? '',
            status: editingSub?.status ?? 'active', owner: editingSub?.owner ?? '', notes: editingSub?.notes ?? '',
          }}
          enableReinitialize
          onSubmit={(values) => {
            onSubmit({
              vendorId: values.vendorId, newVendorName: values.newVendorName, name: values.name,
              amount: Number(values.amount), currency: values.currency, billingCycle: values.billingCycle,
              startDate: values.startDate, nextBilling: values.nextBilling || undefined,
              seats: values.seats ? Number(values.seats) : undefined, status: values.status,
              owner: values.owner || undefined, notes: values.notes || undefined,
            });
          }}
        >
          {({ values, handleChange, setFieldValue }) => (
            <Form>
              <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                <Field as={TextField} name="vendorId" label="Vendor" select required fullWidth size="small" value={values.vendorId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleChange(e); if (e.target.value !== '__new__') setFieldValue('newVendorName', ''); }}>
                  <MenuItem value="">Select vendor...</MenuItem>
                  <MenuItem value="__new__" sx={{ fontStyle: 'italic', color: '#054AD9' }}>+ Create new vendor</MenuItem>
                  {vendors.map((v) => <MenuItem key={v.id} value={v.id}>{v.displayName || v.name}</MenuItem>)}
                </Field>
                {values.vendorId === '__new__' && (
                  <Field as={TextField} name="newVendorName" label="New Vendor Name" required fullWidth size="small" placeholder="e.g. Notion, Figma, AWS" value={values.newVendorName} onChange={handleChange} />
                )}
                <Field as={TextField} name="name" label="Subscription Name" required fullWidth size="small" placeholder="e.g. GitHub Enterprise" value={values.name} onChange={handleChange} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Field as={TextField} name="amount" label="Amount" required type="number" size="small" sx={{ flex: 1 }} value={values.amount} onChange={handleChange} />
                  <Field as={TextField} name="billingCycle" label="Billing Cycle" select size="small" sx={{ flex: 1 }} value={values.billingCycle} onChange={handleChange}>
                    <MenuItem value="monthly">Monthly</MenuItem><MenuItem value="quarterly">Quarterly</MenuItem><MenuItem value="annual">Annual</MenuItem>
                  </Field>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Field as={TextField} name="startDate" label="Start Date" type="date" size="small" sx={{ flex: 1 }} InputLabelProps={{ shrink: true }} value={values.startDate} onChange={handleChange} />
                  <Field as={TextField} name="nextBilling" label="Next Billing" type="date" size="small" sx={{ flex: 1 }} InputLabelProps={{ shrink: true }} value={values.nextBilling} onChange={handleChange} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Field as={TextField} name="seats" label="Seats" type="number" size="small" sx={{ flex: 1 }} value={values.seats} onChange={handleChange} />
                  <Field as={TextField} name="status" label="Status" select size="small" sx={{ flex: 1 }} value={values.status} onChange={handleChange}>
                    <MenuItem value="active">Active</MenuItem><MenuItem value="trial">Trial</MenuItem><MenuItem value="paused">Paused</MenuItem><MenuItem value="cancelled">Cancelled</MenuItem>
                  </Field>
                </Box>
                <Field as={TextField} name="owner" label="Owner" fullWidth size="small" placeholder="Who manages this?" value={values.owner} onChange={handleChange} />
                <Field as={TextField} name="notes" label="Notes" fullWidth size="small" multiline rows={2} value={values.notes} onChange={handleChange} />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onCloseForm} sx={{ textTransform: 'none' }}>Cancel</Button>
                <TrioButton type="submit" variantColor="blue" size="small" disabled={!values.name || !values.vendorId || !values.amount || (values.vendorId === '__new__' && !values.newVendorName) || isSubmitting}>
                  {(() => {
                    if (isSubmitting) return <CircularProgress size={20} />;
                    return editingSub ? 'Update' : 'Create';
                  })()}
                </TrioButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Confirm Cancel Dialog */}
      <Dialog open={!!confirmCancel} onClose={() => setConfirmCancel(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to cancel <strong>{confirmCancel?.name}</strong>? It will be marked as cancelled but kept in records.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmCancel(null)} sx={{ textTransform: 'none' }}>No, Keep It</Button>
          <TrioButton variantColor="yellow" size="small" onClick={() => { if (confirmCancel) onCancel(confirmCancel); setConfirmCancel(null); }}>
            Yes, Cancel
          </TrioButton>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to permanently delete <strong>{confirmDelete?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: 'none' }}>No, Keep It</Button>
          <TrioButton variantColor="red" size="small" onClick={() => { if (confirmDelete) onDelete(confirmDelete); setConfirmDelete(null); }}>
            Yes, Delete
          </TrioButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinanceXSubscriptionsComponent;
